import React from 'react';
import PanelVerticalTransition from '../../transitions/PanelVertical';
import KeyForm from '../KeyForm';
import KeyField from '../KeyField';
import KeyControl from '../KeyControl';
import KeyThankYou from '../KeyThankYou';
import AnimatedSVG from '../AnimatedSVG';

function applyVisibilityStyle(style, visible) {
  let newStyle = {};

  if (!visible) {
    newStyle = {
      ...style,
      opacity: 0
    };
  } else {
    newStyle = {
      ...style,
      opacity: 1
    };
  }

  return newStyle;
}

function applyDisplayStyle(style, show) {
  let newStyle = {};

  if (show) {
    newStyle = {
      ...style,
      display: style.display ? style.display : 'block'
    };
  } else {
    newStyle = {
      ...style,
      display: 'none'
    };
  }

  return newStyle;
}

function wrapperStyle({ styles, color }) {
  const style = {
    ...styles.toEdgeStyle,
    backgroundColor: color.grey['100']
  };

  return style;
}

function callToActionWrapperStyle({ mode }) {
  const visible = mode === 2;
  const show = mode >= 1 && mode <= 3;

  let style = {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    transition: `all 600ms ease-in-out`
  };

  style = applyVisibilityStyle(style, visible);
  style = applyDisplayStyle(style, show);

  return style;
}

function callToActionStyle({ styles, color }) {
  const style = {
    ...styles.fontStyle,
    fontSize: 28,
    color: color.black,
    fontWeight: 300,
    textAlign: 'center',
    marginTop: 72,
    marginRight: 144,
    marginBottom: 72,
    marginLeft: 144
  };

  return style;
}

function fieldWrapperStyle({ mode, dimensions, constants }) {
  const { height } = dimensions;
  const halfHeight = height / 2;
  const visible = mode >= 2 && mode <= 5;
  const show = mode >= 1 && mode <= 6;
  const toTop = mode >= 4;

  let style = {
    position: 'absolute',
    top: halfHeight - constants.KEY_FIELD_HEIGHT,
    right: 0,
    left: 0,
    height: constants.KEY_FIELD_HEIGHT,
    transition: `all 600ms ease-in-out`
  };

  style = applyVisibilityStyle(style, visible);
  style = applyDisplayStyle(style, show);

  if (toTop) {
    style = {
      ...style,
      top: 0
    };
  }

  return style;
}

function keyFieldButtonStyle({ styles, mode }) {
  const show = mode <= 2;

  let style = {
    ...styles.toEdgeStyle
  };

  style = applyDisplayStyle(style, show);

  return style;
}

function controlWrapperStyle({ mode, dimensions, constants }) {
  const { height } = dimensions;
  const halfHeight = height / 2;
  const visible = mode >= 2 && mode <= 5;
  const show = mode >= 1 && mode <= 6;
  const toBottom = mode >= 4;

  let style = {
    position: 'absolute',
    top: halfHeight,
    right: 0,
    left: 0,
    height: constants.KEY_CONTROL_HEIGHT,
    transition: `all 600ms ease-in-out`
  };

  style = applyVisibilityStyle(style, visible);
  style = applyDisplayStyle(style, show);

  if (toBottom) {
    style = {
      ...style,
      top: height - constants.KEY_CONTROL_HEIGHT
    };
  }

  return style;
}

function footerWrapperStyle({ mode }) {
  const visible = mode === 2;

  let style = {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    height: 96,
    transition: `all 600ms ease-in-out`
  };

  style = applyVisibilityStyle(style, visible);

  return style;
}

function keyFormWrapperStyle({ styles, mode }) {
  const visible = mode >= 5 && mode <= 6;
  const show = mode >= 4 && mode <= 7;

  let style = Object.assign({}, styles.toEdgeStyle, styles.centerStyle, {
    transition: `all 600ms ease-in-out`
  });

  style = applyVisibilityStyle(style, visible);
  style = applyDisplayStyle(style, show);

  return style;
}

function checkWrapperStyle({ mode, dimensions, constants }) {
  const show = mode >= 7;
  const visible = mode >= 8;
  const toTop = mode >= 9;
  const { height } = dimensions;
  const halfHeight = height / 2;
  const halfCheckSize = constants.KEY_CHECK_SIZE / 2;

  let style = {
    height: constants.KEY_CHECK_SIZE,
    width: constants.KEY_CHECK_SIZE,
    position: 'absolute',
    top: halfHeight - halfCheckSize,
    left: '50%',
    marginLeft: -halfCheckSize,
    transition: `all 600ms ease-in-out`
  };

  style = applyVisibilityStyle(style, visible);
  style = applyDisplayStyle(style, show);

  if (toTop) {
    style = {
      ...style,
      top: halfHeight - constants.KEY_CHECK_SIZE - constants.KEY_CHECK_SIZE - 96
    };
  }

  return style;
}

function thankYouWrapperStyle({ mode, dimensions, constants }) {
  const visible = mode === 10;
  const show = mode >= 9;
  const { height } = dimensions;
  const halfHeight = height / 2;

  let style = {
    height: constants.KEY_THANK_YOU_HEIGHT,
    transition: `all 600ms ease-in-out`,
    position: 'absolute',
    top: halfHeight - constants.KEY_CHECK_SIZE,
    right: 0,
    left: 0
  };

  style = applyVisibilityStyle(style, visible);
  style = applyDisplayStyle(style, show);

  return style;
}

function createInfoMarkup({ text }) {
  return { __html: text.SIGN_KEYPAD_INFO };
}

export default props => (
  <PanelVerticalTransition
    active={props.mode > 0}
    width={window.innerWidth / 2}
    duration={600}
  >
    <div style={wrapperStyle(props)}>
      <div style={callToActionWrapperStyle(props)}>
        <div
          style={callToActionStyle(props)}
          dangerouslySetInnerHTML={createInfoMarkup(props)}
        />
      </div>
      <div style={footerWrapperStyle(props)} />
      <div style={keyFormWrapperStyle(props)}>
        <KeyForm
          text={props.text}
          color={props.color}
          styles={props.styles}
          constants={props.constants}
          valid={props.valid}
          number={props.number}
          submit={props.submit}
          clear={props.clear}
          redirect={props.redirect}
          add={props.add}
          remove={props.remove}
        />
      </div>
      <div style={fieldWrapperStyle(props)}>
        <KeyField
          text={props.text}
          color={props.color}
          styles={props.styles}
          constants={props.constants}
          number={props.number}
        />
        <div
          style={keyFieldButtonStyle(props)}
          onClick={() => props.showKeys()}
          onKeyPress={() => props.showKeys()}
          tabIndex={0}
          role="button"
        />
      </div>
      <div style={controlWrapperStyle(props)}>
        <KeyControl
          text={props.text}
          color={props.color}
          styles={props.styles}
          constants={props.constants}
          redirect={props.redirect}
        />
      </div>
      <div style={checkWrapperStyle(props)}>
        {props.mode >= 8 && (
          <AnimatedSVG
            visible
            path="/loaders/confirm_01.json"
            size={props.constants.KEY_CHECK_SIZE}
          />
        )}
      </div>
      <div style={thankYouWrapperStyle(props)}>
        <KeyThankYou
          styles={props.styles}
          text={props.text}
          color={props.color}
          constants={props.constants}
          redirect={props.redirect}
        />
      </div>
    </div>
  </PanelVerticalTransition>
);



// WEBPACK FOOTER //
// ./src/components/Keypad/index.js